import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type PagoLocal } from '../datos/db.js'
import { agendaDelDia } from '../datos/consultas.js'
import { fechaCorta, guaranies, hora, hoy, sumarDias } from '../datos/formato.js'
import { Boton } from '../comunes/Boton.js'
import {
  FormularioCargo,
  FormularioPago,
  type ServicioMin,
} from '../comunes/FormulariosCobro.js'

/**
 * Cargos y pagos (T09).
 *
 * **No hay caja centralizada** (§6.1): el dinero puede entrar por el mostrador o
 * directo al profesional. Lo que el sistema registra es el hecho, no un flujo
 * de caja único.
 *
 * Y "pagado" no es un booleano sino una resta. Con seguro, el paciente pone el
 * copago y el resto queda pendiente del tercero, que paga a 30, 60 o 90 días
 * (§6.3). Por eso cada cargo muestra su saldo y quién lo debe.
 *
 * **Por qué es una lista y no un formulario por turno.** Antes cada cita sin
 * cargo mostraba su propio formulario abierto: seis turnos eran seis bloques
 * idénticos repitiendo "Servicio / Monto / botón", media pantalla de campos
 * vacíos para completar uno solo. Ahora la fila cerrada mide 40 px y muestra lo
 * que hace falta para decidir —hora, nombre, saldo—; el formulario aparece al
 * abrirla, y **sólo una fila puede estar abierta a la vez**.
 *
 * Todo esto se escribe **sin conexión**: §5.2 marca cargos y pagos como
 * append-only, sin exclusividad. Cobrar no puede quedar esperando a que vuelva
 * la red.
 */


/**
 * Cómo llegar a la ficha del paciente desde una fila de cobro.
 *
 * Va por contexto y no por props: la cabecera está tres componentes más abajo y
 * arrastrar el handler por `Grupo` y por cada `Fila` ensucia cuatro firmas para
 * un solo clic.
 */
const IrAFicha = createContext<((clienteId: string) => void) | null>(null)

/**
 * Los dos números de la cabecera compartida de Dinero: Cobrado hoy y Por
 * cobrar. Se calculan acá porque viven de las mismas tablas que los grupos de
 * la lista —citas, cargos y pagos—, y `Dinero` los importa: así el total de
 * arriba y las filas de abajo no pueden contradecirse, que es justo el absurdo
 * que T26 quiere matar.
 */
export function useTotalesDelDia(dia: string): { cobrado: number; porCobrar: number } {
  const citas = useLiveQuery(() => agendaDelDia(dia), [dia], [])
  const cargos = useLiveQuery(() => db.cargo.toArray(), [], [])
  const pagos = useLiveQuery(() => db.pago.toArray(), [], [])

  return useMemo(() => {
    const vistas = new Set<string>()
    let cobrado = 0
    let porCobrar = 0
    for (const c of citas) {
      if (vistas.has(c.cita.id)) continue
      vistas.add(c.cita.id)
      // Misma semántica que la lista: sólo se cobra lo atendido.
      if (!['atendida', 'facturada', 'en_atencion'].includes(c.cita.estado)) continue
      const susCargos = cargos.filter((g) => g.cita_id === c.cita.id)
      if (susCargos.length === 0) continue
      const total = susCargos.reduce((a, g) => a + g.monto, 0)
      const pagado = susCargos.reduce(
        (a, g) => a + pagos.filter((p) => p.cargo_id === g.id).reduce((x, p) => x + p.monto, 0),
        0,
      )
      cobrado += pagado
      porCobrar += Math.max(0, total - pagado)
    }
    return { cobrado, porCobrar }
  }, [citas, cargos, pagos])
}

/**
 * "Ver ficha" desde una fila de cobro.
 *
 * Va en el cuerpo abierto de la fila y no en la fila cerrada porque la fila
 * cerrada **entera** es un `<button>` que la despliega: un botón dentro de otro
 * botón es HTML inválido, el navegador desarma el anidado y el clic termina
 * donde nadie lo pidió.
 */
function EnlaceFicha({ clienteId }: { clienteId: string }) {
  const irAFicha = useContext(IrAFicha)
  if (!irAFicha) return null
  return (
    <Boton tamano="sm" variante="ghost" onClick={() => irAFicha(clienteId)}>
      Ver ficha
    </Boton>
  )
}

export interface CobrosProps {
  /** El día que se está mirando. Lo administra `App`, compartido con la agenda. */
  dia: string
  onDia: (dia: string) => void
  /** La cita que hay que abrir al llegar desde otra pantalla. */
  citaEnfocada?: string | null
  onVerPaciente?: ((clienteId: string) => void) | undefined
}

export function Cobros({ dia, onDia, citaEnfocada, onVerPaciente }: CobrosProps) {
  /** La única fila abierta. `null` = todas cerradas. */
  const [abierta, setAbierta] = useState<string | null>(null)

  // Se llega acá desde la atención, la ficha o un pendiente del reporte, y
  // siempre por una cita concreta: abrirla es el motivo del viaje. Que el
  // efecto dependa sólo de la prop es lo que permite cerrar la fila a mano sin
  // que se vuelva a abrir sola en el próximo render.
  useEffect(() => {
    if (citaEnfocada) setAbierta(citaEnfocada)
  }, [citaEnfocada])

  const citas = useLiveQuery(() => agendaDelDia(dia), [dia], [])
  const cargos = useLiveQuery(() => db.cargo.toArray(), [], [])
  const pagos = useLiveQuery(() => db.pago.toArray(), [], [])
  const servicios = useLiveQuery(() => db.servicio.toArray(), [], [])
  const clientes = useLiveQuery(() => db.cliente.toArray(), [], [])

  const pagadoPorCargo = useMemo(() => {
    const mapa = new Map<string, { total: number; deTercero: number }>()
    for (const p of pagos) {
      const actual = mapa.get(p.cargo_id) ?? { total: 0, deTercero: 0 }
      actual.total += p.monto
      if (p.pagador_tipo === 'tercero') actual.deTercero += p.monto
      mapa.set(p.cargo_id, actual)
    }
    return mapa
  }, [pagos])

  /** Las citas del día que ya se atendieron: son las que se cobran. */
  const atendidas = useMemo(() => {
    const vistas = new Set<string>()
    return citas
      .filter((c) => {
        if (vistas.has(c.cita.id)) return false
        vistas.add(c.cita.id)
        return ['atendida', 'facturada', 'en_atencion'].includes(c.cita.estado)
      })
      .map((c) => {
        const susCargos = cargos.filter((g) => g.cita_id === c.cita.id)
        const total = susCargos.reduce((a, g) => a + g.monto, 0)
        const cobrado = susCargos.reduce((a, g) => a + (pagadoPorCargo.get(g.id)?.total ?? 0), 0)
        const cliente = clientes.find((x) => x.id === c.cita.cliente_id)
        return {
          ...c,
          cargos: susCargos,
          total,
          cobrado,
          saldo: total - cobrado,
          conCobertura: (cliente?.cobertura_id ?? null) !== null,
        }
      })
  }, [citas, cargos, pagadoPorCargo, clientes])

  const sinCobrar = atendidas.filter((a) => a.cargos.length === 0)
  const conSaldo = atendidas.filter((a) => a.cargos.length > 0 && a.saldo > 0)
  const saldadas = atendidas.filter((a) => a.cargos.length > 0 && a.saldo <= 0)

  const alternar = (id: string) => setAbierta((actual) => (actual === id ? null : id))

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <p className="text-[15px] font-medium text-slate-900">
          {fechaCorta(`${dia}T12:00:00Z`)}
        </p>
        <div className="flex items-center gap-1">
          {dia !== hoy() && (
            <Boton variante="secondary" onClick={() => onDia(hoy())}>
              Hoy
            </Boton>
          )}
          <Boton
            variante="ghost"
            onClick={() => onDia(sumarDias(dia, -1))}
            aria-label="Día anterior"
            className="w-9 px-0 text-lg"
          >
            ‹
          </Boton>
          <Boton
            variante="ghost"
            onClick={() => onDia(sumarDias(dia, 1))}
            aria-label="Día siguiente"
            className="w-9 px-0 text-lg"
          >
            ›
          </Boton>
        </div>
      </div>

      {atendidas.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white py-12 text-center">
          <p className="txt-body">Todavía no se atendió a nadie este día.</p>
          <p className="txt-meta mt-1">
            Los cobros aparecen cuando una cita pasa a &laquo;atendida&raquo;.
          </p>
        </div>
      )}

      <IrAFicha.Provider value={onVerPaciente ?? null}>
        <Grupo titulo="Sin cargo registrado" citas={sinCobrar} destacar>
          {(cita) => (
            <FilaSinCargo
              cita={cita}
              servicios={servicios}
              abierta={abierta === cita.cita.id}
              onAlternar={() => alternar(cita.cita.id)}
              onListo={() => setAbierta(null)}
            />
          )}
        </Grupo>

        <Grupo titulo="Con saldo pendiente" citas={conSaldo}>
          {(cita) => (
            <FilaConSaldo
              cita={cita}
              servicios={servicios}
              pagos={pagos}
              pagadoPorCargo={pagadoPorCargo}
              abierta={abierta === cita.cita.id}
              onAlternar={() => alternar(cita.cita.id)}
            />
          )}
        </Grupo>

        <Grupo titulo="Saldadas" citas={saldadas} tenue>
          {(cita) => <FilaSaldada cita={cita} servicios={servicios} />}
        </Grupo>
      </IrAFicha.Provider>
    </div>
  )
}

interface CitaCobro {
  cita: { id: string; cliente_id: string; estado: string }
  cliente: { nombre: string } | null
  tramo: { inicio: string; fin: string }
  recursos: string[]
  cargos: { id: string; monto: number; servicio_id: string }[]
  total: number
  cobrado: number
  saldo: number
  conCobertura: boolean
}

function Grupo({
  titulo,
  citas,
  children,
  destacar,
  tenue,
}: {
  titulo: string
  citas: CitaCobro[]
  children: (c: CitaCobro) => React.ReactNode
  destacar?: boolean
  tenue?: boolean
}) {
  if (citas.length === 0) return null

  return (
    <section className="mb-6">
      <h2
        className={`mb-2 text-[13px] font-medium ${
          destacar ? 'text-amber-800' : tenue ? 'text-slate-400' : 'text-slate-600'
        }`}
      >
        {titulo} ({citas.length})
      </h2>
      <ul
        className={`divide-y divide-slate-100 overflow-hidden rounded-xl border bg-white ${
          destacar ? 'border-amber-200' : 'border-slate-200'
        } ${tenue ? 'opacity-70' : ''}`}
      >
        {citas.map((c) => (
          <li key={c.cita.id}>{children(c)}</li>
        ))}
      </ul>
    </section>
  )
}

/** La fila cerrada: 40 px, lo justo para decidir si se abre. */
function Cabecera({
  cita,
  derecha,
  onClick,
  abierta,
}: {
  cita: CitaCobro
  derecha: React.ReactNode
  onClick?: () => void
  abierta?: boolean
}) {
  const contenido = (
    <>
      <span className="w-12 shrink-0 text-[13px] tabular-nums text-slate-500">
        {hora(cita.tramo.inicio)}
      </span>
      <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-slate-900">
        {cita.cliente?.nombre ?? 'Sin cliente'}
      </span>
      {derecha}
    </>
  )

  if (!onClick) {
    return <div className="flex h-10 items-center gap-3 px-4">{contenido}</div>
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={abierta}
      className={`flex h-10 w-full items-center gap-3 px-4 text-left transition hover:bg-slate-50 ${
        abierta ? 'bg-slate-50' : ''
      }`}
    >
      {contenido}
    </button>
  )
}

function FilaSinCargo({
  cita,
  servicios,
  abierta,
  onAlternar,
  onListo,
}: {
  cita: CitaCobro
  servicios: ServicioMin[]
  abierta: boolean
  onAlternar: () => void
  onListo: () => void
}) {
  return (
    <>
      <Cabecera
        cita={cita}
        abierta={abierta}
        onClick={onAlternar}
        derecha={<span className="txt-meta shrink-0">{abierta ? 'cerrar' : 'cargar'}</span>}
      />
      {abierta && (
        <>
          <FormularioCargo
            citaId={cita.cita.id}
            recursosCita={cita.recursos}
            servicios={servicios}
            onListo={onListo}
          />
          <div className="px-4 pb-3">
            <EnlaceFicha clienteId={cita.cita.cliente_id} />
          </div>
        </>
      )}
    </>
  )
}

function FilaConSaldo({
  cita,
  servicios,
  pagos,
  pagadoPorCargo,
  abierta,
  onAlternar,
}: {
  cita: CitaCobro
  servicios: ServicioMin[]
  pagos: PagoLocal[]
  pagadoPorCargo: Map<string, { total: number; deTercero: number }>
  abierta: boolean
  onAlternar: () => void
}) {
  const deTercero =
    cita.conCobertura || cita.cargos.some((g) => (pagadoPorCargo.get(g.id)?.deTercero ?? 0) > 0)

  return (
    <>
      <Cabecera
        cita={cita}
        abierta={abierta}
        onClick={onAlternar}
        derecha={
          <>
            {/* La distinción no es contable: lo que debe un paciente se reclama
                en el mostrador, lo que debe un seguro con una factura y un
                teléfono (§6.3). */}
            <span className="txt-meta hidden shrink-0 sm:inline">
              {deTercero ? 'del seguro' : 'del paciente'}
            </span>
            <span className="w-32 shrink-0 text-right text-[13px] tabular-nums text-amber-700">
              falta {guaranies(cita.saldo)}
            </span>
          </>
        }
      />
      {abierta && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3">
          {cita.cargos.map((cargo) => {
            const pagado = pagadoPorCargo.get(cargo.id) ?? { total: 0, deTercero: 0 }
            const saldo = cargo.monto - pagado.total
            const nombre = servicios.find((s) => s.id === cargo.servicio_id)?.nombre ?? 'Servicio'
            const pagosDeCargo = pagos.filter((p) => p.cargo_id === cargo.id)

            return (
              <div key={cargo.id} className="mb-3 last:mb-0">
                <div className="mb-2 flex items-baseline gap-3">
                  <span className="flex-1 text-[14px] text-slate-700">{nombre}</span>
                  <span className="w-28 text-right text-[13px] tabular-nums text-slate-500">
                    {guaranies(cargo.monto)}
                  </span>
                  <span className="w-32 text-right text-[13px] tabular-nums text-slate-500">
                    pagado {guaranies(pagado.total)}
                  </span>
                </div>
                {/* Cada pago, con dónde entró: dos filas iguales de Gs. 150.000
                    pueden ser una que fue al cajón y otra que se llevó el
                    profesional, y el arqueo no las puede tratar igual. */}
                {pagosDeCargo.length > 0 && (
                  <ul className="mb-2 space-y-1 border-t border-slate-100 pt-1">
                    {pagosDeCargo.map((p) => (
                      <li key={p.id} className="flex items-baseline justify-end gap-3">
                        <span className="txt-meta min-w-0 flex-1 truncate">{p.medio}</span>
                        <span className="txt-meta shrink-0">
                          {etiquetaDestino(p.destino)}
                        </span>
                        <span className="shrink-0 text-[13px] tabular-nums text-slate-600">
                          {guaranies(p.monto)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {saldo > 0 && (
                  <FormularioPago
                    cargoId={cargo.id}
                    saldo={saldo}
                    sugerirTercero={deTercero}
                    destinoInicial="caja"
                  />
                )}
              </div>
            )
          })}

          <div className="mt-3 border-t border-slate-200 pt-2">
            <EnlaceFicha clienteId={cita.cita.cliente_id} />
          </div>
        </div>
      )}
    </>
  )
}

/** Dónde entró la plata, para la fila del pago. Sin esto el arqueo no cierra. */
function etiquetaDestino(destino: PagoLocal['destino']): string {
  if (destino === 'caja') return 'mostrador'
  if (destino === 'profesional') return 'al profesional'
  if (destino === 'aseguradora') return 'del seguro'
  return ''
}

function FilaSaldada({ cita, servicios }: { cita: CitaCobro; servicios: ServicioMin[] }) {
  const nombres = cita.cargos
    .map((g) => servicios.find((s) => s.id === g.servicio_id)?.nombre)
    .filter(Boolean)
    .join(', ')

  return (
    <Cabecera
      cita={cita}
      derecha={
        <>
          <span className="txt-meta hidden min-w-0 flex-1 truncate sm:block">{nombres}</span>
          <span className="w-32 shrink-0 text-right text-[13px] tabular-nums text-slate-500">
            {guaranies(cita.total)}
          </span>
          <EnlaceFicha clienteId={cita.cita.cliente_id} />
        </>
      }
    />
  )
}
